import React, {useState, useEffect, useRef} from 'react'
import axios from 'axios'
import 'bootstrap-icons/font/bootstrap-icons.css'
import {isS3Path} from '../../../../Utils/fileUrlHelper'

const API_URL = process.env.REACT_APP_API_URL

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg']

function getExtensionFromUrl(url: string): string {
  try {
    const pathname = new URL(url).pathname
    const ext = pathname.split('.').pop()?.toLowerCase()
    return ext || ''
  } catch {
    const ext = url.split('.').pop()?.split('?')[0]?.toLowerCase()
    return ext || ''
  }
}

function isImageFile(url: string): boolean {
  return IMAGE_EXTENSIONS.includes(getExtensionFromUrl(url))
}

async function fetchFileBlob(fileUrl: string): Promise<{blob: Blob; contentType: string}> {
  if (isS3Path(fileUrl)) {
    const response = await axios.post(
      `${API_URL}/files/download`,
      {filePath: fileUrl},
      {responseType: 'blob'}
    )
    const contentType =
      typeof response.headers['content-type'] === 'string'
        ? response.headers['content-type']
        : 'application/octet-stream'
    return {blob: response.data, contentType}
  }

  const response = await fetch(fileUrl)
  if (!response.ok) throw new Error('File not found')
  const blob = await response.blob()
  return {blob, contentType: blob.type || 'application/octet-stream'}
}

interface OSIDocumentViewerProps {
  fileUrl: string
  previewText: string
  fileName?: string
  showPreview?: boolean
}

const OSIDocumentViewer: React.FC<OSIDocumentViewerProps> = ({
  fileUrl,
  fileName,
  showPreview = true,
  previewText,
}) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [resolvedUrl, setResolvedUrl] = useState<string>('')
  const [resolvedContentType, setResolvedContentType] = useState<string>('')
  const blobUrlRef = useRef<string | null>(null)

  const revokeBlobUrl = (): void => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = null
    }
  }

  useEffect(() => {
    return () => {
      revokeBlobUrl()
    }
  }, [])

  const handleDownload = async (): Promise<void> => {
    try {
      const {blob} = await fetchFileBlob(fileUrl)
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.setAttribute('download', fileName || 'document')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(downloadUrl)
    } catch (err) {
      setError('Failed to download file')
    }
  }

  const handlePreview = (): void => {
    setIsPreviewOpen(true)
  }

  useEffect(() => {
    const resolveAndCheck = async (): Promise<void> => {
      setIsLoading(true)
      setError('')
      revokeBlobUrl()

      try {
        const {blob, contentType} = await fetchFileBlob(fileUrl)
        const blobUrl = URL.createObjectURL(blob)
        blobUrlRef.current = blobUrl
        setResolvedUrl(blobUrl)
        setResolvedContentType(contentType)
        setIsLoading(false)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'File not available')
        setIsLoading(false)
      }
    }

    if (fileUrl) {
      resolveAndCheck()
    }
  }, [fileUrl])

  if (!fileUrl) return <div className='text-muted'>No file attached</div>

  const showAsImage =
    isImageFile(fileUrl) || resolvedContentType.startsWith('image/')

  return (
    <div className='pdf-handler'>
      {isLoading && (
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      )}

      {error && <div className='alert alert-danger'>{error}</div>}

      {!error && !isLoading && resolvedUrl && (
        <>
          {showPreview && !isPreviewOpen && (
            <div
              className='pdf-thumbnail mt-2'
              style={{
                cursor: 'pointer',
                border: '1px solid #ccc',
                overflow: 'hidden',
                width: '100%',
                height: '200px',
              }}
              onClick={handlePreview}
            >
              {showAsImage ? (
                <img
                  src={resolvedUrl}
                  alt='Document Thumbnail'
                  style={{width: '100%', height: '100%', objectFit: 'contain'}}
                />
              ) : (
                <iframe
                  title='PDF Thumbnail'
                  src={resolvedUrl}
                  style={{border: 'none', width: '100%', height: '100%', pointerEvents: 'none'}}
                />
              )}
            </div>
          )}
          <div className='btn-group mt-2'>
            <button
              onClick={handleDownload}
              className='btn btn-outline-secondary btn-sm'
              type='button'
            >
              <i className='bi bi-download me-2'></i>
              Download
            </button>
            {showPreview && (
              <button
                onClick={handlePreview}
                className='btn btn-outline-secondary btn-sm'
                type='button'
              >
                <i className='bi bi-eye me-2'></i>
                {previewText || 'Preview'}
              </button>
            )}
          </div>
        </>
      )}

      {isPreviewOpen && resolvedUrl && (
        <div
          className='modal fade show d-block'
          style={{backgroundColor: 'rgba(0,0,0,0.5)'}}
          role='dialog'
        >
          <div className='modal-dialog modal-xl'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>{fileName || 'Document Preview'}</h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => setIsPreviewOpen(false)}
                  aria-label='Close'
                ></button>
              </div>
              <div className='modal-body'>
                {showAsImage ? (
                  <div className='text-center'>
                    <img
                      src={resolvedUrl}
                      alt='Document Preview'
                      style={{maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain'}}
                    />
                  </div>
                ) : (
                  <div className='ratio ratio-16x9'>
                    <iframe
                      title='PDF Preview'
                      src={resolvedUrl}
                      style={{border: 'none', width: '100%', height: '100%'}}
                    />
                  </div>
                )}
                <div className='mt-3 text-center'>
                  <button
                    className='btn btn-secondary'
                    onClick={() => setIsPreviewOpen(false)}
                    type='button'
                  >
                    <i className='bi bi-x-lg me-2'></i>
                    Close Preview
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default OSIDocumentViewer
